// OrderTradesPopup.jsx — Shows fill details (individual trades) for a specific order
// Displays: timestamp, quantity, price for each partial fill
// Footer shows total quantity and weighted average price
// Built with Tailwind CSS

import React from 'react';
import { fmtNum } from './calculations';

function OrderTradesPopup({ order, trades, loading, isDark, onClose }) {
  if (!order) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50" onClick={onClose}>
      <div className={`rounded-lg w-96 shadow-2xl ${isDark ? 'bg-app-card border border-app-border' : 'bg-white border border-gray-200'}`} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className={`px-4 py-3 flex justify-between items-center border-b ${isDark ? 'border-app-border' : 'border-gray-200'}`}>
          <div>
            <span className="font-bold text-sm">{order.symbol || order.underlying}</span>
            <span className={`ml-2 text-xs ${isDark ? 'text-app-text-muted' : 'text-gray-500'}`}>{order.strike} {order.type}</span>
            <span className={`ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded ${isDark ? 'bg-[#1a2e1a] text-accent-green' : 'bg-green-50 text-green-600'}`}>TRADES</span>
          </div>
          <button onClick={onClose} className={`text-lg border-none bg-transparent cursor-pointer ${isDark ? 'text-app-text-muted' : 'text-gray-400'}`}>✕</button>
        </div>

        {/* Order summary */}
        <div className={`px-4 py-2 text-xs border-b ${isDark ? 'border-app-border text-app-text-muted' : 'border-gray-200 text-gray-500'}`}>
          Order ID: <span className="font-mono text-[10px]">{order.orderId}</span>
          <span className="mx-2">·</span>
          {order.transactionType} {fmtNum(order.filledQuantity)} / {fmtNum(order.quantity)} filled
        </div>

        {/* Trades table */}
        <div className="px-4 py-3" style={{ maxHeight: 300, overflowY: 'auto' }}>
          {loading ? (
            <div className={`text-center text-xs py-4 ${isDark ? 'text-app-text-muted' : 'text-gray-500'}`}>Loading trades...</div>
          ) : trades.length === 0 ? (
            <div className={`text-center text-xs py-4 ${isDark ? 'text-app-text-dim' : 'text-gray-400'}`}>No fill details available</div>
          ) : (
            <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr className={isDark ? 'text-app-text-muted' : 'text-gray-500'}>
                  <th className="text-left py-1 font-semibold">Time</th>
                  <th className="text-right py-1 font-semibold">Qty</th>
                  <th className="text-right py-1 font-semibold">Price</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((trade, i) => {
                  let timeStr = '';
                  if (trade.timestamp) {
                    try {
                      const d = new Date(trade.timestamp);
                      timeStr = !isNaN(d.getTime())
                        ? d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
                        : trade.timestamp.split(' ').pop() || trade.timestamp;
                    } catch { timeStr = trade.timestamp; }
                  }
                  return (
                    <tr key={trade.tradeId || i} className={`border-t ${isDark ? 'border-app-border' : 'border-gray-100'}`}>
                      <td className={`py-1.5 ${isDark ? 'text-app-text-muted' : 'text-gray-500'}`}>{timeStr || '—'}</td>
                      <td className={`py-1.5 text-right font-medium ${isDark ? 'text-app-text' : 'text-gray-800'}`}>{fmtNum(trade.quantity)}</td>
                      <td className={`py-1.5 text-right font-medium ${isDark ? 'text-app-text' : 'text-gray-800'}`}>₹{Number(trade.price).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className={`border-t-2 ${isDark ? 'border-app-border' : 'border-gray-300'}`}>
                  <td className={`py-1.5 font-semibold ${isDark ? 'text-app-text-muted' : 'text-gray-500'}`}>Total</td>
                  <td className={`py-1.5 text-right font-bold ${isDark ? 'text-app-text' : 'text-gray-800'}`}>
                    {fmtNum(trades.reduce((sum, t) => sum + (t.quantity || 0), 0))}
                  </td>
                  <td className={`py-1.5 text-right font-bold ${isDark ? 'text-app-text' : 'text-gray-800'}`}>
                    {(() => {
                      const totalQty = trades.reduce((s, t) => s + (t.quantity || 0), 0);
                      const totalVal = trades.reduce((s, t) => s + (t.quantity || 0) * (t.price || 0), 0);
                      return totalQty > 0 ? `₹${(totalVal / totalQty).toFixed(2)}` : '—';
                    })()}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrderTradesPopup;
